import { apolloClient } from './ApolloClient';
import { gql } from '@apollo/client';
import type { CreateCustomerInput, UpdateCustomerInput } from '../types/customer';

/**
 * Parameters for list operations
 */
interface ListCustomersParams {
  limit: number;
  nextToken?: string;
}

/**
 * Parameters for create operation
 */
interface CreateCustomerParams {
  customer: CreateCustomerInput;
}

/**
 * Parameters for update operation
 */
interface UpdateCustomerParams {
  customerId: string;
  customer: UpdateCustomerInput;
}

/**
 * Parameters for delete operation
 */
interface DeleteCustomerParams {
  customerId: string;
}

/**
 * Parameters for admin delete operation
 */
interface AdminDeleteCustomerParams {
  customerId: string;
}

/**
 * Parameters for CSV upload URL operation
 */
interface GetCsvUploadUrlParams {
  name: string;
}

/**
 * Service class for customer data operations using AWS AppSync GraphQL API
 */
export class CustomersService {
  /**
   * List customers owned by the current user
   * @param params - Pagination parameters
   * @returns Promise with customers and pagination token
   */
  async listMyCustomers(params: ListCustomersParams) {
    const LIST_MY_CUSTOMERS = gql`
      query ListMyCustomers($limit: Int!, $nextToken: String) {
        listMyCustomers(limit: $limit, nextToken: $nextToken) {
          customers {
            customerId
            primaryEmail
            secondaryEmail
            customerNr
            organization
            company
            company2
            country
            language
            telephone
            firstName
            surName
            pkwData
            lkwData
            createdAt
            updatedAt
            updatedBy
            owner
            validationState
          }
          nextToken
        }
      }
    `;

    const result = await apolloClient.query({ 
      query: LIST_MY_CUSTOMERS, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }

  /**
   * List all customers (admin only)
   * @param params - Pagination parameters
   * @returns Promise with customers and pagination token
   */
  async listAllCustomers(params: ListCustomersParams) {
    const LIST_ALL_CUSTOMERS = gql`
      query ListAllCustomers($limit: Int!, $nextToken: String) {
        listAllCustomers(limit: $limit, nextToken: $nextToken) {
          customers {
            customerId
            primaryEmail
            secondaryEmail
            customerNr
            organization
            company
            company2
            country
            language
            telephone
            firstName
            surName
            pkwData
            lkwData
            createdAt
            updatedAt
            updatedBy
            owner
            validationState
          }
          nextToken
        }
      }
    `;

    const result = await apolloClient.query({ 
      query: LIST_ALL_CUSTOMERS, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }

  /**
   * Create a new customer
   * @param params - Customer data
   * @returns Promise with created customer
   */
  async createCustomer(params: CreateCustomerParams) {
    const CREATE_CUSTOMER = gql`
      mutation CreateCustomer($customer: CreateCustomerInput!) {
        createCustomer(customer: $customer) {
          __typename
          customerId
          primaryEmail
          secondaryEmail
          customerNr
          organization
          company
          company2
          country
          language
          telephone
          firstName
          surName
          pkwData
          lkwData
          createdAt
          updatedAt
          validationState
        }
      }
    `;

    const result = await apolloClient.mutate({ 
      mutation: CREATE_CUSTOMER, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }

  /**
   * Update an existing customer
   * @param params - Customer ID and updated data
   * @returns Promise with updated customer
   */
  async updateCustomer(params: UpdateCustomerParams) {
    const UPDATE_CUSTOMER = gql`
      mutation updateCustomer ($customerId: ID!, $customer: UpdateCustomerInput!) {
        updateCustomer(customerId: $customerId, customer: $customer) {
          company
          company2
          country
          createdAt
          customerId
          customerNr
          firstName
          language
          lkwData
          organization
          pkwData
          primaryEmail
          secondaryEmail
          surName
          telephone
          updatedAt
          updatedBy
          validationState
        }
      }
    `;

    const result = await apolloClient.mutate({ 
      mutation: UPDATE_CUSTOMER, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }

  /**
   * Delete a customer (owner only)
   * @param params - Customer ID
   * @returns Promise with deleted customer number
   */
  async deleteCustomer(params: DeleteCustomerParams) {
    const DELETE_CUSTOMER = gql`
      mutation deleteCustomer ($customerId: ID!) {
        deleteCustomer(customerId: $customerId) {
          customerNr
        }
      }
    `;

    const result = await apolloClient.mutate({ 
      mutation: DELETE_CUSTOMER, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }

  /**
   * Delete a customer as admin (can delete any customer)
   * @param params - Customer ID
   * @returns Promise with deleted customer number
   */
  async adminDeleteCustomer(params: AdminDeleteCustomerParams) {
    const ADMIN_DELETE_CUSTOMER = gql`
      mutation adminDeleteCustomer ($customerId: ID!) {
        adminDeleteCustomer(customerId: $customerId) {
          customerNr
        }
      }
    `;

    const result = await apolloClient.mutate({ 
      mutation: ADMIN_DELETE_CUSTOMER, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }

  /**
   * Get a presigned URL for CSV upload to S3
   * @param params - File name
   * @returns Promise with presigned URL
   */
  async getCsvUploadUrl(params: GetCsvUploadUrlParams) {
    const GET_CSV_UPLOAD_URL = gql`
      query getCsvUploadUrl ($name: String!) {
        getCsvUploadUrl(name: $name)
      }
    `;

    const result = await apolloClient.query({ 
      query: GET_CSV_UPLOAD_URL, 
      variables: params 
    });

    return {
      data: result.data,
      errors: result.errors
    };
  }
}

export default CustomersService;
